/**
 * Package Advance Collection Feature Configuration
 *
 * Centralised feature flags, timing constants, and configurable labels
 * for the package advance collection flow in the invoice settle pipeline.
 *
 * When an invoice has `advance_consumption_metadata` (i.e. items linked to a
 * package) and `status` is partially_paid/unsettled, the system directs the
 * user to collect advance against their package instead of a direct settle
 * payment.  If items are NOT package-related, normal payment is used.
 */

// ============================================================================
// Types
// ============================================================================

export interface PackageAdvanceFeatureConfig {
  /** Enable/disable the entire package advance collection feature */
  enabled: boolean;
  /** Show "Collect Advance Balance" button in the invoice action menu */
  showCollectAdvanceButton: boolean;
  /** When no active packages found, fallback to direct payment instead of blocking */
  fallbackToDirectPayment: boolean;
}

// ============================================================================
// Timing constants (ms)
// ============================================================================

/** Delay between closing one modal and opening the next (smooth transition) */
export const MODAL_TRANSITION_DELAY_MS = 150;

/** Delay before auto-opening the settle modal after advance payment success */
export const POST_ADVANCE_SETTLE_DELAY_MS = 300;

// ============================================================================
// Payment-mode matching
// ============================================================================

/**
 * Name / code used to locate the "Advance" payment mode in the clinic's
 * configured modes.  Compared case-insensitively against `mode.name` and
 * `mode.code`.
 */
export const ADVANCE_PAYMENT_MODE_KEY = 'advance';

// ============================================================================
// Labels (configurable – could later come from i18n or a remote config)
// ============================================================================

/** Method label shown in payment history for package-advance deductions */
export const PKG_ADVANCE_METHOD_LABEL = 'Package Advance';

/** Note attached to auto-generated package-advance payment items */
export const PKG_ADVANCE_NOTES = 'Deducted from package advance';

/** Default advance-against value used when submitting advance payment */
export const DEFAULT_ADVANCE_AGAINST = 'Package Service';

/** Default advance type for the API payload */
export const ADVANCE_TYPE = 'PACKAGE';

/** Payment against type for advance payments */
export const ADVANCE_PAYMENT_AGAINST_TYPE = 'ADVANCE';

/** Payment against type for invoice payments */
export const INVOICE_PAYMENT_AGAINST_TYPE = 'INVOICE';

// ============================================================================
// Defaults
// ============================================================================

const defaultConfig: PackageAdvanceFeatureConfig = {
  enabled: true,
  showCollectAdvanceButton: true,
  fallbackToDirectPayment: true,
};

// ============================================================================
// Accessors
// ============================================================================

export const getPackageAdvanceConfig = (): PackageAdvanceFeatureConfig => {
  const envEnabled = process.env.REACT_APP_PACKAGE_ADVANCE_ENABLED;
  return {
    ...defaultConfig,
    enabled:
      envEnabled !== undefined
        ? envEnabled === 'true'
        : defaultConfig.enabled,
  };
};

// ============================================================================
// Guards
// ============================================================================

/**
 * Internal helper — checks whether a row is linked to a package.
 *
 * The only signal is `advance_consumption_metadata`: if the invoice carries
 * this data the items came from a package and the user should collect advance
 * instead of settling directly.  Bill type is intentionally NOT checked — any
 * invoice type (pharmacy, clinical, etc.) qualifies as long as it has
 * package-linked items.
 *
 * @param hasAdvanceMetadata - Whether the row carries `advance_consumption_metadata`
 * @param requireButtonFlag  - When `true`, also checks `showCollectAdvanceButton`
 */
const isPackageLinkedRow = (
  hasAdvanceMetadata: boolean,
  requireButtonFlag = false,
): boolean => {
  const config = getPackageAdvanceConfig();
  if (!config.enabled) return false;
  if (requireButtonFlag && !config.showCollectAdvanceButton) return false;
  return hasAdvanceMetadata;
};

/**
 * Should the "Collect Advance Balance" menu item be visible for a row?
 *
 * Conditions:
 *  1. Feature is enabled
 *  2. `showCollectAdvanceButton` flag is on
 *  3. Row has `advance_consumption_metadata` (i.e. items linked to a package)
 */
export const shouldShowCollectAdvanceButton = (
  _billType: string | undefined,
  hasAdvanceMetadata: boolean,
): boolean => isPackageLinkedRow(hasAdvanceMetadata, true);

/**
 * Should the settle flow route through package advance validation?
 *
 * Returns true when the invoice has package-linked items.
 * If false the caller should open the normal payment flow.
 */
export const shouldValidatePackageAdvance = (
  _billType: string | undefined,
  hasAdvanceMetadata: boolean,
): boolean => isPackageLinkedRow(hasAdvanceMetadata, false);
